import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ROUTES } from "../../app/routes";
import axios from "axios";

export interface EmpDetails {
  emp_details_uid: number;
  oracle_id: string;
  email_id: string;
  image: string;
  year: string;
  half_year: string;
  current_location: string;
  willingness_to_relocate: string;
  next_role_preference: string;
  future_career_aspirations: string;
  timing_for_next_role: string;
  last_year_top_achievement: string;
  last_year_personal_development_review: string;
  goal_next_two_quarters: string;
  redo_for_previous_business_year?: string; // New field for redo question
  certificates: string;
  education_degree: string;
  created_at: string;
  updated_at: string;
  work_experiences: any[];
  emp_languages: any[];
  EmpLocationPreferences: any[];
  EmpResourceGroup: any[];
  EmpTtecCommunities: any[];
  EmpLeadershipCulture: any[];
}

interface EmpDetailsState {
  data: EmpDetails | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  validationWarnings?: Array<{ field: string; blockedChars: string[]; ruleType: string }>;
}

const initialState: EmpDetailsState = {
  data: null,
  status: "idle",
  error: null,
  validationWarnings: undefined,
};

export const fetchEmpDetails = createAsyncThunk(
  "empDetails/fetchEmpDetails",
  async (emp_id: string, { rejectWithValue }) => {
    try {
      const response: { success: boolean; data: any } = await axios({
        method: "get", 
        url:
          `${ROUTES.getEmpDetailsByEmail}?empId=${emp_id}`,
           withCredentials : true,
        // headers: {
        //   Authorization: `Bearer ${localStorage.getItem("cc-access-token")}`,
        // },
      });
      if (
        response.data &&
        response.data.success === true &&
        response.data.data
      ) {
      
        
        const responseData = response.data.data;
        
        if (!('redo_for_previous_business_year' in responseData)) {
          responseData.redo_for_previous_business_year = "";
        }
        
        if (!('future_career_aspirations' in responseData) || responseData.future_career_aspirations === null) {
          responseData.future_career_aspirations = "";
        }

        if (response.data.validationWarnings) {
          responseData._validationWarnings = response.data.validationWarnings;
        }
        
        return responseData;
      } else if (response.data && response.data.success === false) {
        throw new Error(response.data.message || "API returned success: false");
      } else {
        if (response.data) {
          if (
            response.data.languages ||
            response.data.locations ||
            response.data.resourceGroups ||
            response.data.ttecCommunities ||
            response.data.leadershipCultures
          ) {
            return response.data;
          }
        }
        throw new Error("Unexpected API response format");
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const empDetailsSlice = createSlice({
  name: "empDetails",
  initialState,
  reducers: {
     resetEmpDetails: (state) => {
        state.data= null;
        state.status= "idle";
        state.error= null;
        state.validationWarnings= undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmpDetails.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchEmpDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        
        if (action.payload) {
        
          
          const updatedPayload = {...action.payload};
          if (updatedPayload._validationWarnings) {
            state.validationWarnings = updatedPayload._validationWarnings;
            delete updatedPayload._validationWarnings;
          } else {
            state.validationWarnings = undefined;
          }
          
          if (!('redo_for_previous_business_year' in updatedPayload)) {
            updatedPayload.redo_for_previous_business_year = "";
          }
          
          if (!('future_career_aspirations' in updatedPayload) || updatedPayload.future_career_aspirations === null) {

            updatedPayload.future_career_aspirations = "";
          }
          
          state.data = updatedPayload;
        } else {
          state.data = action.payload;
        }

        if (!action.payload) {
          state.error = "API response did not contain employee details";
        }
      })
      .addCase(fetchEmpDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { resetEmpDetails } = empDetailsSlice.actions;

export default empDetailsSlice.reducer;
